import { RECAPTCHA } from '../resources/constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import Recaptcha from 'react-google-recaptcha';

export const useGoogleCaptcha = (action: 'login' | 'forgotPassword', shouldSkip: boolean) => {
  const [showCaptchaV2, setShowCaptchaV2] = useState(false);
  const captchaRef = useRef<Recaptcha>(null);
  const [isExecutingRecaptcha, setIsExecutingRecaptcha] = useState(false);

  useEffect(() => {
    if (shouldSkip) {
      return () => {};
    }

    // Load reCAPTCHA script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA.V3_SITE_KEY}`;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [shouldSkip]);

  const executeRecaptcha = useCallback(async () => {
    setIsExecutingRecaptcha(true);
    try {
      await window.grecaptcha?.ready(() => {});
      return await window.grecaptcha?.execute(RECAPTCHA.V3_SITE_KEY, {
        action,
      });
    } finally {
      setIsExecutingRecaptcha(false);
    }
  }, [action]);

  const executeRecaptchaV3 = async () => {
    const token = await executeRecaptcha();
    return token;
  };

  // if returns null means it's an error
  const executeRecaptchaV2 = async () => {
    if (!showCaptchaV2) {
      return undefined;
    }

    const recaptchaV2Token = captchaRef.current?.getValue();
    if (!recaptchaV2Token) {
      return null;
    }

    return recaptchaV2Token;
  };

  const execute = async () => {
    if (shouldSkip) {
      return {};
    }

    const recaptchaV2Token = await executeRecaptchaV2();
    // this is a specific case when captchaV2 is needed but errored. If it's not needed then return undefined
    if (recaptchaV2Token === null) {
      return null;
    }

    const recaptchaV3Token = await executeRecaptchaV3();

    return {
      recaptchaV2Token,
      recaptchaV3Token,
    };
  };

  const captchaRendered = showCaptchaV2 ?
    <Recaptcha
      className='mb-1'
      ref={captchaRef}
      sitekey={RECAPTCHA.V2_SITE_KEY}
    />
    : null;

  return {
    captchaRendered,
    execute,
    isExecutingRecaptcha,
    setShowCaptchaV2,
    showCaptchaV2,
  };
};
